import dayjs from "dayjs";
import * as Sentry from "@sentry/nextjs";
import { addAncillaryToOffer, refundAncillaryFromOffer } from "../../../../app/api/getOffers";
import { getUserDetails } from "../../../../app/api/getUserDetails";
import { patchPassenger } from "../../../../app/api/patchPassenger";
import { patchPurchaser } from "../../../../app/api/patchPurchaser";
import { updateCustomerById } from "../../../../app/api/updateCustomerById";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { determinePassengerType, determineIsDOBRequiredForPassengerType } from "../../../../Lib/Utils/validationUtil";
import { isPlaceholderName } from "../../../../Lib/Utils/customerName";
import { buildInvoicingAddress } from "../../../../Lib/Utils/invoiceAddress";
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IDeterminePassenger, IUserResponse } from "../../../../Orchestrator/Types/Index";
import { IAddAncillaryToOfferRequest, IAddonsPassengerLeg, IAlreadyAddonsForPassenger } from "../../../../Orchestrator/Types/Interface/IAddOns";
import { ICustomerType } from "../../../../Orchestrator/Types/Interface/ICustomer";
import { IPaymentProcessActionParams } from "../../../../Orchestrator/Types/Interface/IPaymentProcess";
import {
  IPatchPassengerRequest,
  IPurchaserRequest,
} from "../../../../Orchestrator/Types/Interface/IPurchaserRequest";

export class PaymentProcessAction extends BaseAction<string, IPaymentProcessActionParams> {
  // Coalesce concurrent invocations: a quick double-click on Pay would
  // otherwise re-fire the refund-offers POST against a booking that has
  // already been modified, producing a 400 BOOKING_MODIFICATION_NOT_ALLOWED.
  protected override deduplicate = true;

  async calculateAge(dateOfBirth: Date) {
    const dob = dayjs(dateOfBirth);
    const today = dayjs();
    const age = today.diff(dob, "year");
    return age;
  }

  async fetchUserData(): Promise<IUserResponse> {
    try {
      const isAnonymous: boolean = getRecoilStateValue("isAnonymousStateAtom") as boolean;
      if (isAnonymous && isAnonymous === true) {
        return {} as IUserResponse;
      }
      const response: IApiResponse<IUserResponse> = await getUserDetails(false);
      if (response.status === "ok" && response.answer) {
        const userData: IUserResponse = response.answer;

        const updatedUserData: IUserResponse = {
          ...userData,
          firstName: userData.firstName === "-" ? "" : userData.firstName,
          lastName: userData.lastName === "-" ? "" : userData.lastName,
        };

        updateRecoilState<IUserResponse>("userDataAtom", updatedUserData);

        return response.answer;
      } else {
        console.warn("Failed to fetch user data:", response);
        throw new Error("Failed to fetch user data");
      }
    } catch (error: any) {
      console.error("Error fetching user data:", error);
      throw new Error("Failed to fetch user data");
    }
  }

  /**
   * The contact names for each profile name still holding the "-"
   * placeholder. A name the customer already has is never overwritten, and
   * an empty contact field is never sent.
   */
  namesToFill(
    userData: { firstName?: string | null; lastName?: string | null } | null | undefined,
    contact: { firstName?: string | null; lastName?: string | null } | null | undefined
  ): { firstName?: string; lastName?: string } {
    const names: { firstName?: string; lastName?: string } = {};
    if (isPlaceholderName(userData?.firstName) && !isPlaceholderName(contact?.firstName)) {
      names.firstName = contact!.firstName!.trim();
    }
    if (isPlaceholderName(userData?.lastName) && !isPlaceholderName(contact?.lastName)) {
      names.lastName = contact!.lastName!.trim();
    }
    return names;
  }

  private getDefault<T>(value: T | undefined, defaultValue: T): T {
    return value !== undefined ? value : defaultValue;
  }

  private isSameInvoicingAddress(
    existing: any,
    next: any
  ): boolean {
    if (!existing && !next) return true;
    if (!existing || !next) return false;
    const norm = (v: any) => (v == null ? "" : String(v));
    return (
      norm(existing.name) === norm(next.name) &&
      norm(existing.trn) === norm(next.trn) &&
      norm(existing.street) === norm(next.street) &&
      norm(existing.complement) === norm(next.complement) &&
      norm(existing.city) === norm(next.city) &&
      norm(existing.countryCode) === norm(next.countryCode)
    );
  }

  private async updateCustomerHelper(customerId: string, customerData: Partial<any>) {
    try {
      const response: any = await updateCustomerById(customerId, customerData);
      if (response.status === "ok" && response.answer) {
        const updatedCustomer: IUserResponse = response.answer;
        // PUT response carries the full updated customer — use it as the
        // new userDataAtom value instead of round-tripping a second /me.
        const normalized: IUserResponse = {
          ...updatedCustomer,
          firstName: updatedCustomer.firstName === "-" ? "" : updatedCustomer.firstName,
          lastName: updatedCustomer.lastName === "-" ? "" : updatedCustomer.lastName,
        };
        updateRecoilState<IUserResponse>("userDataAtom", normalized);
      } else {
        throw new Error(
          `Failed to update customer (${response?.code ?? "no status"}): ${response?.error ?? "no error message"}`
        );
      }
    } catch (error: any) {
      // The payment goes on either way, so without this a rejected update
      // (e.g. a 400 on an incomplete invoicingAddress) left no trace and the
      // customer kept the "-" placeholder names. Field names only, no values.
      console.error("Error updating customer:", error);
      Sentry.captureException(error, {
        tags: { feature: "checkout-customer-update" },
        extra: { customerId, fields: Object.keys(customerData ?? {}) },
      });
    }
  }

  async updateBasicInfo(
    customerId: string,
    basicInfo: {
      email?: string;
      firstName?: string;
      lastName?: string;
      birthdate?: string;
      phoneNumber?: string;
      gender?: "MALE" | "FEMALE";
      countryCode?: string;
      preferredLanguage?: string;
      title?: string;
    }
  ) {
    try {
      // Only the fields given: defaulting a missing name to "" wiped it, and
      // a missing preferredLanguage/phone to "" cleared the stored value.
      const updateData = Object.fromEntries(
        Object.entries(basicInfo).filter(([, value]) => value !== undefined)
      );
      if (Object.keys(updateData).length === 0) return;

      await this.updateCustomerHelper(customerId, updateData);
    } catch (error: any) {
      console.error("Error updating basic customer information:", error);
    }
  }

  async updatePassengerList(customerId: string, passengers: Partial<ICustomerType['additionalPassengers']>) {
    const validPassengers = passengers?.map(passenger => {
      const passengerData: any = {
        firstName: this.getDefault(passenger?.firstName, 'Unknown'),
        lastName: this.getDefault(passenger?.lastName, 'Unknown')
      };
      if (passenger?.passengerType) {
        passengerData.passengerType = passenger.passengerType
      }
      if (passenger?.title) {
        passengerData.title = passenger.title
      }

      if (passenger?.birthdate) {
        passengerData.birthdate = passenger.birthdate?.split('T')[0];
      }

      return passengerData;
    }) ?? [];
    await this.updateCustomerHelper(customerId, { additionalPassengers: validPassengers });
  }

  override async run(): Promise<string> {
    let returnString: any = "success";
    try {
      const { passengers, booking, bookingId, customer, updatePersonalInfo, passengerTypes, invoiceDetails, manageAddonsNewConfirmedAddons, manageAddonsRemovedAddons, carLicensePlate } = this.params!;
      updateRecoilState("paymentSuccessModalAtom", false);
      updateRecoilState("selectedTripsDataAtom", null);
      let confirmAddonsList: IAddonsPassengerLeg[] = manageAddonsNewConfirmedAddons || [] as IAddonsPassengerLeg[]
      let removedAddonsList: IAlreadyAddonsForPassenger[] = manageAddonsRemovedAddons || [] as IAlreadyAddonsForPassenger[]
      const isManageSeating = getRecoilStateValue("isManageSeatingAtom") || false
      const patchAddonsOnConfirm =
        (getRecoilStateValue("patchAddonsOnConfirmAtom") as boolean) ?? true;
      // Reuse user data already fetched on page load (PassengerDetails /
      // topbar / GetUserDetailsAction populate userDataAtom). Fall back to
      // /me only if the atom is empty — e.g. anonymous flow that just
      // upgraded to authenticated, or a hard reload that skipped earlier
      // init steps.
      const isAnonymous: boolean = (getRecoilStateValue("isAnonymousStateAtom") as boolean) || false;
      let userData: any = !isAnonymous
        ? (getRecoilStateValue<IUserResponse>("userDataAtom") || null)
        : null;
      if (!isAnonymous && (!userData || !userData.id)) {
        userData = await this.fetchUserData();
      }
      // Guests have no customer record: `userData` is deliberately left null
      // above. Every /customers/{id} write below reads the id through this,
      // because an unguarded `userData.id` threw a TypeError on the guest path
      // and the catch turned that into a bare "fail" the caller could not tell
      // apart from a rejected booking.
      const customerId: string = userData?.id ? String(userData.id) : "";

      // Get passengers that should be remembered
      const rememberedPassengers = passengers.filter(p => p.remember);

      // Convert remembered passengers to AdditionalPassenger format      
      const newAdditionalPassengers = rememberedPassengers.map(passenger => {
        const determinePassenger: IDeterminePassenger = {
          age: passenger?.age,
          cards: passenger?.cards || [],
          prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
        }
        const passengerType = determinePassengerType(determinePassenger);
        const rememberPassType = passengerTypes.find(d => d.id === passengerType)?.value
        return {
          firstName: passenger.detail.firstName,
          lastName: passenger.detail.lastName,
          birthdate: determineIsDOBRequiredForPassengerType(passengerType) ? passenger.dateOfBirth?.toISOString() : null,
          ...rememberPassType ? { passengerType: rememberPassType } : {}
        }
      });

      // Save updated passenger list only if there are new passengers to add      
      if (newAdditionalPassengers.length > 0) {

        const existingPassengers = userData?.additionalPassengers || [];

        const newUniquePassengers = newAdditionalPassengers.filter(
          newPassenger =>
            !existingPassengers.some((existingPassenger: { firstName: string; lastName: string; birthdate: any; passengerType: any; }) =>
              existingPassenger.firstName.trim().toLowerCase() === newPassenger.firstName.trim().toLowerCase() &&
              existingPassenger.lastName.trim().toLowerCase() === newPassenger.lastName.trim().toLowerCase() &&
              (existingPassenger.birthdate ?? "") === (newPassenger.birthdate ?? "") &&
              (existingPassenger.passengerType ?? "") === (newPassenger.passengerType ?? "")
            )
        );

        if (customerId && newUniquePassengers.length > 0) {
          const updatedPassengerList = [...(userData?.additionalPassengers || []), ...newAdditionalPassengers];
          await this.updatePassengerList(customerId, updatedPassengerList);
      }
    }

      // Patch passenger information
      for (const passenger of passengers) {
        const patchRequest: IPatchPassengerRequest = {
          id: passenger.id,
          summary: `${passenger.detail?.firstName || ""} ${passenger.detail?.lastName || ""}`,
          externalRef: passenger.externalRef,
          type: passenger.type,
          detail: {
            preferredLanguage: "en",
            summary: `${passenger.detail.firstName} ${passenger.detail.lastName}`,
            firstName: passenger.detail.firstName,
            lastName: passenger.detail.lastName,
            email: passenger.detail.email || customer.email,
            phoneNumber: passenger.detail.phoneNumber || customer.phoneNumber,
          },
          // age: age, // We can not patch age, because if prebook call with 16 and on checkout if age is calculating other than 16 then after a sales API get fail
          dateOfBirth: passenger?.dateOfBirth
            ? passenger.dateOfBirth instanceof Date
              ? passenger.dateOfBirth.toISOString()
              : new Date(passenger.dateOfBirth).toISOString()
            : "",
        };
        await patchPassenger(patchRequest, bookingId, false);
      }

      if (customer) {
        const purchaserRequest: IPurchaserRequest = {
          externalRef: passengers[0]?.externalRef || "",
          detail: {
            summary: "",
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phoneNumber: customer.phoneNumber,
          },
        };
        try {
          await patchPurchaser(bookingId, purchaserRequest, false);
        } catch {
          // Handle error
        }
      }

      /**
       * Removed ancillary(Addons) from the booked offer
       * First prepare all the ancillary(addons) Is's for all trip
       * Second, refund that all the ancillary.
       * Third, patch the offer into booking.
      */
      if (!patchAddonsOnConfirm && removedAddonsList.length > 0) {
        const allAncillaryFulfillmentIds = removedAddonsList.map(d => d?.ancillaryId || "").filter(d => d != "")
        const request = await refundAncillaryFromOffer(allAncillaryFulfillmentIds, bookingId)
        if (request.status === "ok" && request.code === 200) {
          if (isManageSeating) {
            sessionStorage.setItem("needToShowRemovedAddons", bookingId)
          } else {
            sessionStorage.removeItem("needToShowRemovedAddons")
          }
          const refundOffers = request.answer?.refundOffers || []
          // if (refundOffers.length > 0) {
          //   const allRefundOffersIds = refundOffers.map(d => d.id)
          //   if (allRefundOffersIds.length > 0) {
          //     for (let i = 0; i < allRefundOffersIds.length; i++) {
          //       const refundOfferId = allRefundOffersIds[i];
          //       //await patchRefundOffer(refundOfferId, bookingId)
          //     }
          //     if (isManageSeating) {
          //       sessionStorage.setItem("needToShowRemovedAddons", bookingId)
          //     } else {
          //       sessionStorage.removeItem("needToShowRemovedAddons")
          //     }
          //   }
          // }
        }
      }

      /**
       * Added ancillary(Addons) to booked offer
       * First prepare all the addons passenger based on ancillary for all trip
       * Second, prepare all the ancillary for all the offer
       * Third, prepare payload(API) to add ancillary to offer
      */
      if (!patchAddonsOnConfirm && (confirmAddonsList || []).length > 0) {
        const allTripLegPassengerAddons: IAddAncillaryToOfferRequest[] = []
        confirmAddonsList?.forEach(addonsDetail => {
          addonsDetail.addonsPassenger.forEach(addonsPass => {
            Object.values(addonsPass?.addonItem || {}).forEach(async (addonsItem: any) => {
              const tripLegPassengerAddons: IAddAncillaryToOfferRequest = {
                offerId: addonsItem?.additionalOfferId || "",
                bookedOfferId: addonsItem?.bookedOfferId || "",
                ancillaryOfferId: addonsItem?.ancillaryOfferId || "",
                passengerRefs: [addonsPass.passengerRef],
                tripCoverage: {
                  coveredTripId: addonsItem?.tripCoverageId || ""
                }
              };
              allTripLegPassengerAddons.push(tripLegPassengerAddons)
            })
          })
        })
        if (allTripLegPassengerAddons.length > 0) {
          const mergeOffers = allTripLegPassengerAddons.reduce((acc: any, item: IAddAncillaryToOfferRequest) => {
            const key = `${item.offerId}-${item.ancillaryOfferId}-${item?.tripCoverage?.coveredTripId}`;
            acc[key] = acc[key] || { ...item, passengerRefs: [] };
            acc[key].passengerRefs.push(...item.passengerRefs);
            return acc;
          }, {})

          const mergeDataOfferWise = (Object.values(mergeOffers) as IAddAncillaryToOfferRequest[]).reduce((acc: any, item: IAddAncillaryToOfferRequest) => {
            const key = `${item?.bookedOfferId}`;
            acc[key] = [
              ...acc[key] || [],
              item
            ]
            return acc;
          }, {})

          const allKeys = Object.keys(mergeDataOfferWise)
          const addonsPromises = [];
          for (let i = 0; i < allKeys.length; i++) {
            const offerId = allKeys[i]
            const offerAddons = mergeDataOfferWise[allKeys[i]]
            for (let j = 0; j < offerAddons.length; j++) {
              delete offerAddons[i]?.bookedOfferId
              const request = await addAncillaryToOffer(offerAddons[j], bookingId, offerId)
              addonsPromises.push(request)
            }
          }
          if (addonsPromises.length > 0) {
            await Promise.all(addonsPromises)
            if (isManageSeating) {
              sessionStorage.setItem("needToShowAddedAddons", bookingId)
            } else {
              sessionStorage.removeItem("needToShowAddedAddons")
            }
          }
        }
      }

      if (invoiceDetails && Object.values(invoiceDetails).some(value => value && value.trim())) {
        const invoiceRequestPayload = {
          formData: {
            companyName: invoiceDetails.companyName || "",
            trn: invoiceDetails.trn || "",
            address1: invoiceDetails.address1 || "",
            address2: invoiceDetails.address2 || "",
            city: invoiceDetails.city || "",
            countryBilling: invoiceDetails.countryBilling || ""
          },
          userEmail: customer.email || ""
        };
        localStorage.setItem('invoiceRequestPayload', JSON.stringify(invoiceRequestPayload));
      }

      // check if it's pass booking
      let consumptionType = booking.booking?.bookedOffers?.[0]?.admissions?.[0]?.indicatedConsumption?.unit?.objectType
      const isPassBooking = ["DayTravelAccountUnit", "TripTravelAccountUnit"].includes(consumptionType || "");
      if (isPassBooking) {
        localStorage.setItem("isPassBooking", "true");
      }

      // Only a complete address: the pre-filled country alone made the API
      // reject the whole customer update with a 400.
      const invoicingAddress = customerId ? buildInvoicingAddress(invoiceDetails) : undefined;

      if (updatePersonalInfo && customerId) {
        // Names travel in their own PUT so an invoice or plate problem can
        // never take them down; they are sent below, separately.
        await this.updateBasicInfo(customerId, {
          ...this.namesToFill(userData, customer),
          phoneNumber: customer.phoneNumber,
          email: userData?.email,
        });
      }

      if (invoicingAddress) {
        // Only PUT when something actually changed — invoice details persist
        // across sessions (localStorage), so the form usually re-submits the
        // exact same address that's already on the server.
        const existingPlate = userData?.carLicensePlate || "";
        const plateChanged = !!carLicensePlate && carLicensePlate !== existingPlate;
        const invoiceChanged = !this.isSameInvoicingAddress(
          userData?.invoicingAddress,
          invoicingAddress
        );
        if (customerId && (invoiceChanged || plateChanged)) {
          await this.updateCustomerHelper(customerId, {
            ...(invoiceChanged ? { invoicingAddress } : {}),
            ...(plateChanged ? { carLicensePlate } : {}),
          });
        }
      } else if (carLicensePlate) {
        const existingPlate = userData?.carLicensePlate || "";
        if (customerId && carLicensePlate !== existingPlate) {
          await this.updateCustomerHelper(customerId, {
            carLicensePlate,
          });
        }
      }

      // Refunds for deselected baseline extras are now settled by the
      // backend as part of POST /api/payment/session — the frontend sends
      // the modified ancillaries (added + queued-for-refund) in the body
      // and the backend orchestrates both the new charge and the refund
      // server-side. See buildSessionAncillaries / RedirectToPaymentGateway.

    } catch (error) {
      // Every pre-gateway step (passenger PATCHes, purchaser PATCH, ancillary
      // add/refund, customer update) funnels its failure here and the only
      // trace used to be a console line — the caller just saw "fail". Report
      // it with enough booking context to find the failing request in the
      // backend logs, which is exactly what a multi-passenger acceptance
      // failure needs and never had.
      console.error("Error during checkout payment processing:", error);
      Sentry.captureException(error, {
        tags: { feature: "checkout-payment" },
        extra: {
          bookingId: this.params?.bookingId,
          passengerCount: this.params?.passengers?.length,
        },
      });
      returnString = "fail"
    } finally {
      updateRecoilState("addonFlowStartedAtom", false);
      return returnString
    }
  }
}

export const paymentProcessActionInstance = new PaymentProcessAction();
